/**
 * OverlapMatrix — the pairwise heatmap (Phase 2).
 *
 * A real table: ETF on both axes, the numeric percentage inside every cell.
 * Colour is redundant on purpose (design.md §14: never information by colour
 * alone) — the number, the label and the aria-label carry the meaning, the
 * sequential palette only speeds up reading.
 */
import { AlertTriangle } from "lucide-react";

const BANDS = [
  { max: 10, className: "bg-chart-seq-1 text-content", label: "0–10 %" },
  { max: 30, className: "bg-chart-seq-2 text-content", label: "10–30 %" },
  { max: 60, className: "bg-chart-seq-3 text-content-on-brand", label: "30–60 %" },
  { max: 85, className: "bg-chart-seq-4 text-content-on-brand", label: "60–85 %" },
  { max: 100.01, className: "bg-chart-seq-5 text-content-on-brand", label: "85–100 %" },
];

const bandFor = (value) => BANDS.find((band) => value < band.max) || BANDS[BANDS.length - 1];

const fmt = (value) => `${value.toFixed(value >= 10 ? 0 : 1)}%`;

export function OverlapMatrix({ matrix, onSelectPair }) {
  const { subjects, cells, dimension_label: dimensionLabel } = matrix;
  const byPair = new Map();
  cells.forEach((cell) => {
    byPair.set(`${cell.a}:${cell.b}`, cell);
    byPair.set(`${cell.b}:${cell.a}`, cell);
  });

  return (
    <div className="space-y-3" data-testid="overlap-matrix">
      <div
        className="overflow-x-auto rounded-lg border border-line"
        data-testid="overlap-matrix-scroll"
      >
        <table className="border-collapse text-label">
          <caption className="sr-only">
            Sovrapposizione strutturale fra gli ETF per {dimensionLabel}
          </caption>
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-20 bg-surface px-2 py-2 text-left font-medium
                           text-content-muted border-b border-r border-line min-w-[104px]"
              >
                ETF
              </th>
              {subjects.map((subject) => (
                <th
                  key={subject.key}
                  scope="col"
                  className="bg-surface px-1 py-2 font-mono font-medium text-content-secondary
                             border-b border-line min-w-[62px]"
                  title={subject.label}
                >
                  {subject.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {subjects.map((rowSubject, i) => (
              <tr key={rowSubject.key}>
                <th
                  scope="row"
                  className="sticky left-0 z-10 bg-surface px-2 py-1.5 text-left font-mono
                             font-medium text-content-secondary border-r border-line whitespace-nowrap"
                >
                  <span className="block">{rowSubject.key}</span>
                  <span
                    data-testid={`overlap-coverage-${rowSubject.key}`}
                    className="block text-label font-sans text-content-muted"
                  >
                    cov. {rowSubject.coverage_pct.toFixed(0)}%
                  </span>
                </th>
                {subjects.map((colSubject, j) => {
                  if (i === j) {
                    // A dimension without a comparable base is not a measured
                    // 100 %: the self-comparison follows the no_data policy.
                    const measurable = rowSubject.coverage_pct > 0;
                    return (
                      <td
                        key={colSubject.key}
                        data-testid={`overlap-cell-diagonal-${i}`}
                        className={`border border-line text-center tabular px-1 py-1.5
                                    ${measurable
                                      ? "bg-surface-subtle text-content-muted"
                                      : "bg-chart-missing text-content-muted [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,hsl(var(--line))_3px,hsl(var(--line))_4px)]"}`}
                        aria-label={measurable
                          ? `${rowSubject.key} confrontato con se stesso: 100%`
                          : `${rowSubject.key}: nessuna esposizione classificata su ${dimensionLabel}`}
                      >
                        {measurable ? "100%" : "n/d"}
                      </td>
                    );
                  }
                  const cell = byPair.get(`${i}:${j}`);
                  if (!cell) return <td key={colSubject.key} />;
                  const unsupported = !cell.supported || cell.classified_overlap_pct === null;
                  const value = cell.classified_overlap_pct;
                  const band = unsupported ? null : bandFor(value);
                  const aria = unsupported
                    ? `${rowSubject.key} e ${colSubject.key}: dati insufficienti su ${dimensionLabel}, `
                      + `coverage ${rowSubject.coverage_pct.toFixed(0)} % e ${colSubject.coverage_pct.toFixed(0)} %`
                    : `${rowSubject.key} e ${colSubject.key}: sovrapposizione ${fmt(value)} `
                      + `su ${dimensionLabel}, affidabilità ${cell.reliability === "high" ? "alta" : "media"}`;
                  return (
                    <td key={colSubject.key} className="border border-line p-0">
                      <button
                        type="button"
                        data-testid={`overlap-cell-${i}-${j}`}
                        onClick={() => onSelectPair(i, j)}
                        aria-label={aria}
                        className={`w-full min-h-10 px-1 py-1.5 text-center tabular transition-colors
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-focus/60
                                    ${unsupported
                                      ? "bg-chart-missing text-content-muted [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,hsl(var(--line))_3px,hsl(var(--line))_4px)]"
                                      : `${band.className} hover:opacity-80`}`}
                      >
                        {unsupported ? "n/d" : fmt(value)}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2 text-label text-content-muted"
        data-testid="overlap-legend"
      >
        <span className="font-medium text-content-secondary">Sovrapposizione</span>
        {BANDS.map((band) => (
          <span key={band.label} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-4 h-3 rounded-sm border border-line ${band.className}`} />
            {band.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5" data-testid="overlap-legend-missing">
          <span className="inline-block w-4 h-3 rounded-sm border border-line bg-chart-missing
                           [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,hsl(var(--line))_3px,hsl(var(--line))_4px)]" />
          n/d — dati insufficienti
        </span>
      </div>

      {(matrix.warnings || []).map((warning) => (
        <p
          key={warning}
          data-testid="overlap-warning"
          role="status"
          className="flex items-start gap-2 text-label text-warning"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          {warning}
        </p>
      ))}
    </div>
  );
}

export default OverlapMatrix;
